import { cn } from '@orcar/common-d2c';
import {
  PET_PREPARATION_CARDS,
  PET_PREPARATION_CLOSE_LABEL,
  PET_PREPARATION_CONFIRM_LABEL,
  PET_PREPARATION_DESCRIPTION,
  PET_PREPARATION_TITLE,
} from '@/constants/pet-policy';

type PetPreparationSheetProps = {
  open: boolean;
  onClose: () => void;
  /** "이해했습니다!" 클릭 시 호출 — 하단 필수 체크를 모두 체크한다. */
  onConfirm: () => void;
  isMobile?: boolean;
};

/**
 * 반려동물 동반 준비사항 안내 시트.
 * - 데스크탑: 화면 중앙 모달
 * - 모바일: 하단에서 올라오는 바텀시트
 */
const PetPreparationSheet = ({
  open,
  onClose,
  onConfirm,
  isMobile = false,
}: PetPreparationSheetProps) => {
  if (!open) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-[1000] flex',
        isMobile ? 'items-end' : 'items-center justify-center',
      )}
    >
      {/* 배경 딤 */}
      <button
        type='button'
        aria-label='닫기'
        onClick={onClose}
        className='absolute inset-0 bg-black/40'
      />

      {/* 시트 본문 */}
      <div
        className={cn(
          'relative flex max-h-[85vh] w-full flex-col bg-grey-000',
          isMobile
            ? 'rounded-t-[20px]'
            : 'mx-4 max-w-[560px] rounded-[20px] shadow-lg',
        )}
      >
        {/* 헤더 */}
        <div className='flex items-start justify-between px-6 pt-6'>
          <div className='flex flex-col gap-1'>
            <p className='sub1 font-bold text-grey-090'>
              {PET_PREPARATION_TITLE}
            </p>
            <p className='body2 text-grey-060'>{PET_PREPARATION_DESCRIPTION}</p>
          </div>
          <button
            type='button'
            aria-label='닫기'
            onClick={onClose}
            className='body1 -mr-1 shrink-0 px-1 text-grey-060'
          >
            ✕
          </button>
        </div>

        {/* 카드 리스트 */}
        <div className='flex flex-col gap-3 overflow-y-auto px-6 py-5'>
          {PET_PREPARATION_CARDS.map((card) => (
            <div
              key={card.title}
              className='flex gap-3 rounded-[12px] border border-grey-020 p-4'
            >
              <div className='flex size-12 shrink-0 items-center justify-center rounded-[10px] bg-grey-005 text-[22px]'>
                {card.icon}
              </div>
              <div className='flex flex-col gap-1.5'>
                <p className='sub3 font-bold text-grey-090'>{card.title}</p>
                <p className='body2 text-grey-070'>{card.description}</p>
                <div className='flex flex-wrap gap-1.5'>
                  {card.tags.map((tag) => (
                    <span
                      key={tag}
                      className='caption1 rounded-full bg-primary-050/[0.08] px-2 py-0.5 text-primary-050'
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 푸터 버튼 */}
        <div className='flex gap-2 px-6 pb-6 pt-2'>
          <button
            type='button'
            onClick={onClose}
            className='sub3 flex-1 rounded-s bg-grey-010 py-3.5 text-grey-070'
          >
            {PET_PREPARATION_CLOSE_LABEL}
          </button>
          <button
            type='button'
            onClick={onConfirm}
            className='sub3 flex-[2] rounded-s bg-primary-050 py-3.5 font-bold text-grey-000'
          >
            {PET_PREPARATION_CONFIRM_LABEL}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PetPreparationSheet;
